// Configuration › Automated messages (routes, GraphQL names and flags still say "notifications"). Copy is plain English
// and hard-coded, like the other configuration features.
// Values the API sends are the engine's own: channels "email" | "sms" | "notification" (push), audiences
// candidate | recruiter | client, topics applications | credentials | timesheets | job_matches | account,
// schedule kinds instant | milestone | repeating, impact levels low | medium | high.

export const CHANNELS = ['email', 'sms', 'notification']
export const CHANNEL_LABELS = { email: 'Email', sms: 'Text message', notification: 'Push notification' }
// The channel inside a sentence: "~210 candidates get this by text".
export const CHANNEL_NOUNS = { email: 'email', sms: 'text', notification: 'push' }
// What a recipient with no other channel lacks: "45 of them have no email or app".
export const CHANNEL_MISSING_NOUNS = { email: 'email', sms: 'texts', notification: 'app' }

export const AUDIENCE_FILTERS = [
	{ value: 'all', label: 'Everyone' },
	{ value: 'candidate', label: 'Candidates' },
	{ value: 'recruiter', label: 'Recruiters' },
	{ value: 'client', label: 'Clients' },
]
export const AUDIENCE_LABELS = { candidate: 'Candidate', recruiter: 'Recruiter', client: 'Client' }
export const AUDIENCE_SINGULARS = { candidate: 'candidate', recruiter: 'recruiter', client: 'client' }
export const AUDIENCE_PLURALS = { candidate: 'candidates', recruiter: 'recruiters', client: 'clients' }
export const AUDIENCE_CHIP_STYLES = {
	candidate: { background: '#ECFDF5', color: '#065F46' },
	recruiter: { background: '#FFF7ED', color: '#9A3412' },
	client: { background: '#F0F9FF', color: '#075985' },
}

export const TOPICS = [
	{ value: 'applications', label: 'Applications' },
	{ value: 'credentials', label: 'Credentials' },
	{ value: 'timesheets', label: 'Timesheets' },
	{ value: 'job_matches', label: 'Job matches' },
	{ value: 'account', label: 'Account & profile' },
]
export const TOPIC_LABELS = TOPICS.reduce((labels, topic) => ({ ...labels, [topic.value]: topic.label }), {})

export const LEVEL_LABELS = { low: 'Low', medium: 'Medium', high: 'High' }
export const LEVEL_STYLES = {
	low: { background: '#EFF4FF', color: '#0F58E4' },
	medium: { background: '#FFF6DE', color: '#B26A00' },
	high: { background: '#FDECEC', color: '#C62828' },
}

// Limits (spec §4.5). SMS warns at one text and is refused past 1,600; push is refused past its limits.
export const SMS_SEGMENT_LENGTH = 160
export const SMS_HARD_LIMIT = 1600
export const PUSH_TITLE_LIMIT = 50
export const PUSH_BODY_LIMIT = 150
export const MONTH_DAYS = Array.from({ length: 28 }, (_, index) => index + 1)
export const TEST_SENDS_PER_HOUR = 20

// Activity export (spec §4.4): built from notificationHistory in the browser, capped.
export const EXPORT_MAX_DAYS = 90
export const EXPORT_MAX_ROWS = 2000

export const ERROR_CODES = { validation: 'VALIDATION_ERROR', reasonRequired: 'REASON_REQUIRED', rateLimited: 'RATE_LIMITED', notFound: 'NOT_FOUND' }

// Amendment A15: "Notifications" read like personal notification settings, so the hub calls them automated messages
// ("message" alone where it reads better). Only the push channel keeps "Push notification".
export const COPY = {
	pageTitle: 'Automated messages',
	intro: 'Emails, texts and push messages Maestro sends automatically when something happens.',
	notPersonal: 'These go to candidates, recruiters and clients — not your personal notification settings.',
	readOnly: 'Only admins can change automated messages',
	locked: 'Turned off by Maestro support',
	replacedBy: (workflow) => `Skipped for candidates who get job alerts from ‘${workflow}’`,
	searchPlaceholder: 'Search automated messages…',
	noMatches: 'No automated messages match your search.',
	// The empty list with nothing searched: a recipient filter with no messages, or none at all.
	noneForAudience: (plural) => `No automated messages go to ${plural}.`,
	// Amendment A16: Maestro support turns each message on for the hub (default off); none is turned on yet.
	noneVisible: 'No automated messages are available yet. Ask Maestro support to turn them on.',
	notSetUp: 'Not set up yet',
	loadFailed: 'Automated messages could not be loaded.',
	rescheduled: 'Messages already waiting are rescheduled; nothing is re-sent.',
	needsChannel: 'Choose at least one channel before turning this on.',
	keepOneChannel: 'Keep at least one channel. To stop this message, turn it off instead.',
	unsaved: 'You have unsaved changes to this message. Leave this page and discard them?',
	// Why "Restore default message" and "Restore this version" wait while the editor has unsaved changes.
	saveFirst: 'Save or discard your changes first.',
	// Draft with AI in the Message card (amendment A13). It only fills the draft; Save changes still saves it.
	aiBriefPlaceholder: 'e.g. Thank them for applying and say a recruiter will reply within 2 business days',
	// Amendment A17: the details Draft with AI writes with, listed in the AI panel.
	aiDetails: 'AI uses this message’s info:',
	// The API answers the allowance but has no draft mutation (an older gem).
	aiUnavailable: 'AI assist isn’t available on this site yet.',
	aiReplaceNote: {
		email: 'A draft replaces the current subject and body; nothing is saved until you click Save changes.',
		sms: 'A draft replaces the current text; nothing is saved until you click Save changes.',
		notification: 'A draft replaces the current title and message; nothing is saved until you click Save changes.',
	},
	// Push and SMS are plain text on the phone: formatting code in them, and Clean up.
	formattingCode: 'This message contains formatting code (like <p>) that phones show as plain text.',
	formattingRemoved: 'Formatting code removed.',
	shortenTo: (limit) => `Now shorten it to ${limit.toLocaleString('en-US')} characters.`,
	// Conditions and the preview (amendment A15).
	conditionIntro: 'Show different text depending on a detail. The condition goes where your cursor was.',
	conditionsSummary: 'This message changes depending on:',
	// A15 review M1: a button's link is an attribute; a condition there would break it.
	noConditionInLink: 'A condition can’t go in a button’s link. Click in the text where it should go.',
	// "Try other values" only when the preview offers values to try.
	conditionsCount: (count, canTry = true) =>
		`This message has ${count} condition${count === 1 ? '' : 's'}.${canTry ? ' Try other values to see each version.' : ''}`,
	tryWithHint: 'Only for this preview; never saved.',
	previewUnavailable: 'The preview is not available right now.',
	previewFooter: 'Details show sample data. Use Send test to check a real inbox.',
	// The preview of a tab whose channel is unticked in Channels and has nothing written: no empty bubble.
	channelOffEmpty: (label) => `${label} is turned off. Turn on ${label} in Channels to send this channel.`,
	// Read out with a tab whose channel is unticked in Channels; "(off)" beside its name shows it.
	channelOffTab: 'Turned off in Channels',
	aiFaq: {
		question: 'Can AI write the message for me?',
		answer:
			'Yes, if you can change automated messages. On the Message card, describe what the message should say and click Draft with AI; for an email, Suggest subjects offers three subject lines. The draft is only a suggestion: check it in the preview and edit it as you like. Each request uses your organization’s monthly AI allowance, the same one workflow templates use. Nothing changes until you click Save changes.',
	},
}
